import React, { useState } from 'react';
import { Check, Eye, EyeOff } from 'lucide-react';
import { passwordChecks, passwordError } from '../utils/passwordPolicy';

/**
 * A password input that says what it wants before you get it wrong.
 *
 * There were five of these across the app, each its own input with
 * `minLength={6}` and a placeholder saying "At least 6 characters" — so the
 * only feedback was the browser's own tooltip, and the real rules only arrived
 * as a 400 after submitting.
 *
 * The checklist appears while typing and stops once every rule is met, so it
 * guides rather than nags. `onValidityChange` lets the form disable submit.
 */
export default function PasswordField({
  value,
  onChange,
  label = 'Password',
  autoFocus = false,
  showChecklist = true,
  onValidityChange,
}) {
  const [touched, setTouched] = useState(false);
  const [visible, setVisible] = useState(false);

  const checks = passwordChecks(value);
  const error = passwordError(value);
  const allMet = checks.every(c => c.ok);

  React.useEffect(() => {
    if (onValidityChange) onValidityChange(!error);
  }, [error, onValidityChange]);

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>

      <div style={{ position: 'relative' }}>
        <input
          className="form-input"
          type={visible ? 'text' : 'password'}
          required
          autoFocus={autoFocus}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="At least 8 characters"
          style={{ width: '100%', boxSizing: 'border-box', paddingRight: 40 }}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: 'var(--text-muted)', display: 'flex',
          }}
        >
          {visible ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>

      {/* Once everything is met the list has nothing left to say, so it goes. */}
      {showChecklist && !allMet && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 7 }}>
          {checks.map(c => (
            <span
              key={c.label}
              style={{
                fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4,
                color: c.ok ? 'var(--green)' : 'var(--text-muted)',
              }}
            >
              {c.ok ? <Check size={11} strokeWidth={3} /> : <span style={{
                width: 9, height: 9, borderRadius: '50%',
                border: '1.5px solid var(--border)', display: 'inline-block',
              }} />}
              {c.label}
            </span>
          ))}
        </div>
      )}

      {/* Only after they have left the field — telling someone their password
          is too short while they are still typing it is just noise. */}
      {touched && error && allMet && (
        <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}
